<script setup lang="ts">
	import {useCookieConsentStore} from '@/scripts/stores/cookie-consent';
	import {trans} from 'laravel-vue-i18n';
	import {Button} from '@/views/components/ui/button';
	import {
		Card,
		CardContent,
		CardDescription,
		CardHeader,
		CardTitle,
	} from '@/views/components/ui/card';
	import {Badge} from '@/views/components/ui/badge';
	import {Settings, Cookie} from 'lucide-vue-next';
	import {computed} from 'vue';

	const cookieStore = useCookieConsentStore();

	const necessaryCount = computed(
		() => cookieStore.categories.filter((c) => c.required).length
	);

	const optionalCount = computed(
		() => cookieStore.categories.filter((c) => !c.required).length
	);

	const enabledOptionalCount = computed(
		() =>
			cookieStore.categories.filter((c) => !c.required && c.enabled)
				.length
	);
</script>

<template>
	<Teleport to="body">
		<div
			v-if="cookieStore.showBanner"
			class="fixed inset-x-0 bottom-0 z-50 p-4 sm:p-6"
		>
			<Card
				class="border-border/50 bg-card/95 mx-auto max-w-4xl border shadow-lg backdrop-blur-sm"
			>
				<CardHeader class="pb-4">
					<div class="flex items-start justify-between gap-4">
						<div class="flex items-center gap-3">
							<div
								class="bg-primary/10 flex h-10 w-10 items-center justify-center rounded-full"
							>
								<Cookie class="text-primary h-5 w-5" />
							</div>
							<div>
								<CardTitle class="text-lg">
									{{ trans('cookie_consent.banner.title') }}
								</CardTitle>
								<div class="mt-1 flex items-center gap-2">
									<Badge
										variant="secondary"
										class="text-xs"
									>
										{{ necessaryCount }}
										{{
											trans(
												'cookie_consent.banner.necessary'
											)
										}}
									</Badge>
									<Badge
										variant="outline"
										class="text-xs"
									>
										{{ enabledOptionalCount }}/{{
											optionalCount
										}}
										{{
											trans(
												'cookie_consent.banner.optional'
											)
										}}
									</Badge>
								</div>
							</div>
						</div>
					</div>
				</CardHeader>

				<CardContent class="space-y-4">
					<CardDescription class="text-sm leading-relaxed">
						{{ trans('cookie_consent.banner.description') }}
						<RouterLink
							:href="route('privacy_policy')"
							class="text-primary ml-1 hover:underline"
						>
							{{
								trans(
									'cookie_consent.banner.privacy_policy_link'
								)
							}}
						</RouterLink>
					</CardDescription>

					<!-- Quick category overview -->
					<div
						class="grid grid-cols-1 gap-3 sm:grid-cols-2 lg:grid-cols-4"
					>
						<div
							v-for="category in cookieStore.categories"
							:key="category.id"
							class="bg-muted/50 border-border/30 flex items-center justify-between rounded-lg border p-3"
						>
							<div>
								<div class="text-sm font-medium">
									{{
										trans(
											`cookie_consent.categories.${category.id}.name`
										)
									}}
								</div>
								<div class="text-muted-foreground text-xs">
									{{
										category.required
											? trans(
													'cookie_consent.banner.required'
												)
											: trans(
													'cookie_consent.banner.optional'
												)
									}}
								</div>
							</div>
							<div class="flex items-center">
								<div
									:class="[
										'h-2 w-2 rounded-full',
										category.enabled
											? 'bg-green-500'
											: category.required
												? 'bg-blue-500'
												: 'bg-gray-400',
									]"
								/>
							</div>
						</div>
					</div>

					<!-- Action buttons -->
					<div class="flex flex-col gap-3 pt-2 sm:flex-row">
						<Button
							@click="cookieStore.acceptNecessary"
							variant="outline"
							class="flex-1 sm:flex-none"
						>
							{{
								trans('cookie_consent.banner.accept_necessary')
							}}
						</Button>

						<Button
							@click="cookieStore.openSettings"
							variant="outline"
							class="flex-1 sm:flex-none"
						>
							<Settings class="mr-2 h-4 w-4" />
							{{ trans('cookie_consent.banner.customize') }}
						</Button>

						<Button
							@click="cookieStore.acceptAll"
							class="flex-1"
						>
							{{ trans('cookie_consent.banner.accept_all') }}
						</Button>
					</div>

					<!-- Legal compliance note -->
					<div
						class="text-muted-foreground border-border/30 border-t pt-3 text-xs"
					>
						<p>
							{{ trans('cookie_consent.banner.gdpr_note') }}
						</p>
					</div>
				</CardContent>
			</Card>
		</div>
	</Teleport>
</template>
